import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Services  } from "assets/SideBarIcons/services.svg";
import StyledInput from "common/SearchField/Search";
import { useAssignAllItemService, useAssignItemService, useGetServiceItemService, useUnassignAllItemService, useUnassignItemService } from 'subModule/src/services/service/useService';
import ServiceTabs from './ServiceTabs';
import { Grid } from '@mui/material';
import { ServiceProfileService } from 'subModule/src/core/services';
import { GetServiceByIdResponse } from 'subModule/src/services/service/interface';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';

export default function ServiceItemsTable() {
    const params = useParams();
    const serviceId = params?.id;
    const navigate = useNavigate();
    const [serviceTitle, setServiceTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [item, setItem] = useState<any>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? 
        useGetServiceItemService({ pageNumber: pageNo, size: pageSize, title: searchTerm }, +(params as any).id, item?.itemGroup === 1 && item?.itemGroupId ? item?.itemGroupId : null) 
        : useGetServiceItemService({ pageNumber: pageNo, size: pageSize }, +(params as any).id, item?.itemGroup === 1 && item?.itemGroupId ? item?.itemGroupId : null);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    let allItemsAssigned: unknown = tableData?.pages[0].data?.content.map(item => item.linked).every(linked => linked === true);
    useEffect(() => {
        getByID();
    }, []);
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        if(Object.keys(item)?.length > 0) {
            fetchData();
        }
    }, [pageNo, pageSize, searchTerm, item]);
    const getByID = () => {
        ServiceProfileService.getServiceById(+(params as any)?.id).then((res: GetServiceByIdResponse) => {
            if (res.success) {
                setItem(res?.data);
                const name = pickProfileEntityTitle(res);
                if (name) setServiceTitle(name);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignItem({ serviceId: +(params as any).id, itemId: row.id });
        } else {
            onUnassignItem({ serviceId: +(params as any).id, itemId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllItems({ serviceId: +(params as any).id });
        } else {
            onUnassignAllItems({ serviceId: +(params as any).id });
        }
    }
    const { onAssignItem } = useAssignItemService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(item => item.linked).length) {
                    allItemsAssigned = true;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('Item') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignItem } = useUnassignItemService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(item => item.linked).length) {
                    allItemsAssigned = false;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('Item') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllItems } = useAssignAllItemService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allItemsAssigned = true;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('items') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllItems } = useUnassignAllItemService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allItemsAssigned = false;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('items') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'services', href: '/services' },
            {
                labelKey: 'title',
                ...(serviceTitle ? { label: serviceTitle } : {}),
                href: serviceId ? `/service/${serviceId}` : undefined,
            },
            { labelKey: 'assignPart' },
        ],
        [serviceId, serviceTitle],
    );

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/services')}
                heading={t('services')}
                icon={<Services height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ServiceTabs params={params} selectedIndex={1} formData={item} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'title', label: 'name', numeric: false },
                            { id: 'itemCost', label: 'itemCost', numeric: false },
                            { id: 'itemCode', label: 'itemCode', numeric: false },
                            { id: 'price', label: 'price', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allItemsAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_SERVICE_ITEM_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_SERVICE_ITEM_UNASSIGN')}
                        headerComponent={<Grid container spacing={2}>
                            <Grid item xs={12} md={7} lg={5} className='mt-3'>
                                <StyledInput
                                    fullWidth
                                    placeholder={t("name")}
                                    handleSearchClick={handleSearchClick}
                                    handleChange={handleInputChange}
                                    value={search}
                                    clearSearch={clearSearch}
                                />
                            </Grid>
                        </Grid>}
                    />
                </div>
            </Box>
        </Box>
    )
}